import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  Button,
  Chip,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";

const show = (v) => {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.filter(Boolean).join(", ") || "—";
  return String(v);
};

const FIELD_LABEL = {
  nome: "colName",
  sku: "colSku",
  preco: "colPrice",
  detalhes: "colDescription",
  peso: "colWeight",
};

const PREVIEW_COLUMNS = [
  { id: "nome", field: "nome", always: true, labelKey: "colName", get: (r) => r.nome },
  { id: "sku", field: "sku", always: true, labelKey: "colSku", get: (r) => r.sku },
  { id: "tipo", field: "tipo", labelKey: "colType", get: (r) => r.tipo },
  { id: "preco", field: "preco", labelKey: "colPrice", get: (r) => r.preco },
  { id: "discount", field: "preco_comdesconto", labelKey: "colDiscount", get: (r) => r.preco_comdesconto },
  { id: "estoque", field: "estoque", labelKey: "colStock", get: (r) => r.estoque },
  { id: "status", field: "status", labelKey: "colStatus", get: (r) => r.status },
  { id: "categoria", field: "categoria_nome", alt: "id_categoria", labelKey: "colCategory", get: (r) => r.categoria_nome || r.id_categoria },
  { id: "subcategoria", field: "subcategoria_nome", alt: "id_subcategoria", labelKey: "colSubcategory", get: (r) => r.subcategoria_nome || r.id_subcategoria },
  { id: "detalhes", field: "detalhes", alt: "descricao_ia", labelKey: "colDescription", ellipsis: 280, get: (r) => r.detalhes },
  { id: "peso", field: "peso", labelKey: "colWeight", get: (r) => r.peso },
  { id: "largura", field: "largura", labelKey: "colWidth", get: (r) => r.largura },
  { id: "altura", field: "altura", labelKey: "colHeight", get: (r) => r.altura },
  { id: "comprimento", field: "comprimento", labelKey: "colLength", get: (r) => r.comprimento },
  { id: "truck", field: "aplicacao_serie_truck", labelKey: "colTruck", get: (r) => r.aplicacao_serie_truck },
  { id: "bus", field: "aplicacao_serie_bus", labelKey: "colBus", get: (r) => r.aplicacao_serie_bus },
  { id: "imagens", field: "imagens_urls", labelKey: "colImages", ellipsis: 220, get: (r) => r.imagens_urls },
  {
    id: "inclusos",
    field: "inclusos_itens",
    labelKey: "colIncluded",
    ellipsis: 280,
    get: (r) => (Array.isArray(r.inclusos_itens) ? r.inclusos_itens.join("; ") : r.inclusos_itens),
  },
];

const ImportDialog = ({
  open,
  rows,
  applying,
  remapping = false,
  filledFields = [],
  ambiguousFields = [],
  columnChoices = {},
  onChangeColumnChoice,
  onClose,
  onApply,
}) => {
  const { t } = useTranslation("products");
  const statusMeta = useMemo(
    () => ({
      new: {
        label: t("importDialog.statusNew"),
        color: "#e8f5e9",
        chipColor: "success",
      },
      update: {
        label: t("importDialog.statusUpdate"),
        color: "#fff8e1",
        chipColor: "warning",
      },
      error: {
        label: t("importDialog.statusError"),
        color: "#ffebee",
        chipColor: "error",
      },
      saving: {
        label: t("importDialog.statusSaving"),
        color: "#e3f2fd",
        chipColor: "info",
      },
      saved: {
        label: t("importDialog.statusSaved"),
        color: "#e8f5e9",
        chipColor: "success",
      },
      failed: {
        label: t("importDialog.statusFailed"),
        color: "#ffebee",
        chipColor: "error",
      },
    }),
    [t]
  );

  const stats = rows.reduce(
    (acc, r) => {
      const st = r.__status || "new";
      acc[st] = (acc[st] || 0) + 1;
      return acc;
    },
    { new: 0, update: 0, error: 0, saving: 0, saved: 0, failed: 0 }
  );
  const readyCount = (stats.new || 0) + (stats.update || 0) + (stats.failed || 0);
  const sending = (stats.saving || 0) > 0;
  const finished = (stats.saved || 0) > 0 || (stats.failed || 0) > 0;
  const done = finished && !applying && readyCount === 0;
  const filled = new Set(filledFields);
  const visibleCols = PREVIEW_COLUMNS.filter(
    (col) => col.always || filled.has(col.field) || (col.alt && filled.has(col.alt))
  );

  const busy = applying || remapping;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t("importDialog.title")}</DialogTitle>

      <DialogContent dividers>
        <Alert severity={stats.failed ? "warning" : sending ? "info" : finished ? "success" : "info"} sx={{ mb: 2 }}>
          {sending
            ? t("importDialog.sendingIntro")
            : done
              ? t("importDialog.resultDone", { saved: stats.saved || 0 })
              : finished
                ? t("importDialog.resultIntro", { saved: stats.saved || 0, failed: stats.failed || 0 })
                : t("importDialog.infoIntro")}
        </Alert>

        {ambiguousFields.length > 0 && !sending && !done ? (
          <Box
            sx={{
              mb: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            {remapping ? <LinearProgress sx={{ height: 3 }} /> : <Box sx={{ height: 3 }} />}
            <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
              <Typography variant="subtitle2">
                {t("importDialog.mappingTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75 }}>
                {t("importDialog.mappingHint")}
              </Typography>
              {ambiguousFields.map((group) => {
                const selected = columnChoices[group.field] || group.columns[0]?.header || "";
                return (
                  <Box key={group.field} sx={{ "& + &": { mt: 2 } }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600, letterSpacing: 0.3 }}>
                      {t(`importDialog.${FIELD_LABEL[group.field] || "colName"}`)}
                    </Typography>
                    <RadioGroup
                      row
                      value={selected}
                      onChange={(e) => onChangeColumnChoice?.(group.field, e.target.value)}
                      sx={{ gap: 1.25, flexWrap: "wrap" }}
                    >
                      {group.columns.map((col) => {
                        const active = selected === col.header;
                        return (
                          <Box
                            key={col.header}
                            sx={{
                              flex: "1 1 280px",
                              maxWidth: 440,
                              border: "1px solid",
                              borderColor: active ? "primary.main" : "divider",
                              borderRadius: 2,
                              bgcolor: active ? "action.selected" : "background.paper",
                            }}
                          >
                            <FormControlLabel
                              value={col.header}
                              disabled={busy}
                              sx={{
                                m: 0,
                                p: 1.25,
                                pr: 1.5,
                                alignItems: "flex-start",
                                width: "100%",
                                gap: 0.5,
                              }}
                              control={<Radio size="small" sx={{ mt: 0.15 }} />}
                              label={
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="subtitle2" sx={{ lineHeight: 1.3, wordBreak: "break-word" }}>
                                    {col.header}
                                  </Typography>
                                  {col.sample ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, wordBreak: "break-word" }}>
                                      {t("importDialog.mappingSample", { sample: col.sample })}
                                    </Typography>
                                  ) : null}
                                  {col.filled != null ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                      {t("importDialog.mappingFilled", { count: col.filled })}
                                    </Typography>
                                  ) : null}
                                </Box>
                              }
                            />
                          </Box>
                        );
                      })}
                    </RadioGroup>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null}

        <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Chip size="small" label={`${t("importDialog.create")}: ${stats.new || 0}`} sx={{ bgcolor: statusMeta.new.color }} />
          <Chip size="small" label={`${t("importDialog.update")}: ${stats.update || 0}`} sx={{ bgcolor: statusMeta.update.color }} />
          <Chip size="small" label={`${t("importDialog.saved")}: ${stats.saved || 0}`} sx={{ bgcolor: statusMeta.saved.color }} />
          <Chip size="small" label={`${t("importDialog.errors")}: ${(stats.error || 0) + (stats.failed || 0)}`} sx={{ bgcolor: statusMeta.error.color }} />
        </Box>

        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              maxHeight: 460,
              overflow: "auto",
              opacity: remapping ? 0.45 : 1,
              pointerEvents: remapping ? "none" : "auto",
            }}
          >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {visibleCols.map((col) => (
                  <TableCell key={col.id}>{t(`importDialog.${col.labelKey}`)}</TableCell>
                ))}
                <TableCell>{t("importDialog.actionCol")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => {
                const st = r.__status || "new";
                const meta = statusMeta[st] || statusMeta.new;
                const errors = Array.isArray(r.__errors) ? r.__errors.filter(Boolean) : [];
                return (
                  <TableRow key={i} sx={{ backgroundColor: meta.color }}>
                    {visibleCols.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={
                          col.ellipsis
                            ? {
                                maxWidth: col.ellipsis,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }
                            : undefined
                        }
                      >
                        {show(col.get(r))}
                      </TableCell>
                    ))}
                    <TableCell sx={{ minWidth: 160 }}>
                      <Chip size="small" label={meta.label} color={meta.chipColor} variant="outlined" />
                      {errors.length > 0 ? (
                        <Typography variant="caption" sx={{ display: "block", color: "#b71c1c", mt: 0.5 }}>
                          {errors.join(" • ")}
                        </Typography>
                      ) : null}
                      {r.__saveHint ? (
                        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                          {r.__saveHint}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>
          {remapping ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(18,18,18,0.55)" : "rgba(255,255,255,0.7)",
                zIndex: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <CircularProgress size={22} />
                <Typography variant="body2">{t("importDialog.mappingUpdating")}</Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseRoundedIcon />} disabled={busy} variant={done ? "contained" : "text"}>
          {done ? t("importDialog.close") : t("importDialog.cancel")}
        </Button>
        {done ? null : (
          <Button variant="contained" onClick={onApply} disabled={busy || readyCount === 0}>
            {applying
              ? t("importDialog.applying")
              : (stats.saved || 0) > 0 && readyCount > 0
                ? t("importDialog.retry")
                : t("importDialog.applyImport")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
